import { Field, FieldLabel } from "@nt/ui/field";
import { CircleAlert } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router";

import {
  RESET_PASSWORD_PATH,
  resetLinkError,
  resetPassword,
} from "../auth/password-reset";
import { AuthLayout } from "./auth-layout";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@nt/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Where the emailed reset link lands: `/reset-password?token=…`, or
 * `?error=INVALID_TOKEN` when the auth Worker already refused the token.
 * Outside the gate, like the forgot-password screen.
 */
export function ResetPassword() {
  const { search } = useLocation();
  const linkError = resetLinkError(search);
  const token = new URLSearchParams(search).get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    const outcome = await resetPassword({ token, newPassword, confirm });
    setPending(false);
    if (outcome.ok) setDone(true);
    else setError(outcome.message);
  }

  if (linkError) {
    return (
      <AuthLayout
        title="Reset your password"
        subtitle="This link can no longer be used."
      >
        <div className="space-y-6">
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>{linkError}</AlertTitle>
          </Alert>
          <a
            href="/forgot-password"
            className={buttonVariants({ size: "lg", className: "w-full" })}
          >
            Send a new reset link
          </a>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout
        title="Password changed"
        subtitle="Your password has been changed."
      >
        <a
          href="/"
          className={buttonVariants({ size: "lg", className: "w-full" })}
        >
          Sign in
        </a>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Set a new password for your account."
    >
      <div className="space-y-6">
        {error ? (
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}

        <form
          action={RESET_PASSWORD_PATH}
          method="post"
          onSubmit={(event) => void submit(event)}
          className="space-y-4"
        >
          <Field>
            <FieldLabel htmlFor="new-password">New password</FieldLabel>
            <Input
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="h-11"
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="confirm-password">
              Confirm new password
            </FieldLabel>
            <Input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              className="h-11"
            />
          </Field>
          <Button type="submit" size="lg" className="w-full" disabled={pending}>
            {pending ? "Saving…" : "Set new password"}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
